import { c as _c } from "react/compiler-runtime";
import "@/styles/globals.css";
import "destyle.css/destyle.min.css";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import type { ErrorInfo } from "react";
import { useContext, useEffect } from "react";
import { ApolloProvider } from "@apollo/client";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { OpenFeatureProvider } from "@openfeature/react-sdk";
import * as Sentry from "@sentry/nextjs";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { ErrorBoundary } from "react-error-boundary";

//
// ページごとに Layout を選択できるようにする（see https://nextjs.org/docs/basic-features/layouts#with-typescript）
import { inspectorAuthInteractor } from "@/client/amplify/auth";
import { client } from "@/client/graphql";
import { DefaultError } from "@/components/uis/DefaultError";
import { IncidentBanner } from "@/components/uis/IncidentBanner";
import { Loading } from "@/components/uis/Loading";
import { AssessmentConfigProvider } from "@/hooks/assessmentConfig/AssessmentConfigProvider";
import { AssessmentListProvider } from "@/hooks/global-state/assessmentList";
import { ConnectDataProvider } from "@/hooks/global-state/connectData";
import { LoadingContext, LoadingMutationProvider } from "@/hooks/global-state/loadingContext";
import { ReceiptListProvider } from "@/hooks/global-state/receiptList";
import { ScrollPositionProvider } from "@/hooks/global-state/scrollPosition";
import { SearchParamsContextProvider } from "@/hooks/global-state/searchParamsContextProvider";
import { SearchParamsContextProviderNew } from "@/hooks/global-state/searchParamsContextProviderNew";
import { SnackbarContext, SnackbarProvider } from "@/hooks/global-state/snackbar";
import { useDebugModeEnabled } from "@/hooks/storage/useDebugModeEnabled";
import { useViewModeFlg } from "@/hooks/storage/useViewModeFlg";
import { useReloadIfNewVersionFound } from "@/hooks/utils/useReloadIfNewAppVersionFound";
import { initializeFeatureFlags } from "@/utils/featureflag/featureflag";
import { SignedCookieGate } from "@/utils/featureflag/SignedCookieGate";
import { theme } from "@/utils/mui/theme";
import "@/utils/analysis/analysis"; // Amplitude の読み込み

import { SWRConfig } from "swr";
import { analysisEventKey, flushAnalysisEvents, trackAnalysisEvent } from "@/utils/analysis/event";
import type { NextPageWithLayout } from "@/utils/layout/types";
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

/** 監視モード帯の高さ(px)。AdminLayout の VIEW_MODE_BANNER_HEIGHT と一致させる */
const VIEW_MODE_BANNER_HEIGHT = 50;
/** デバッグモード帯の高さ(px) */
const DEBUG_MODE_BANNER_HEIGHT = 32;
function MyApp(t0) {
  const $ = _c(37);
  const {
    Component,
    pageProps
  } = t0;
  useReloadIfNewVersionFound();
  const router = useRouter();
  let t1;
  let t2;
  if ($[0] !== router.events) {
    t1 = () => {
      const handleRouteChangeComplete = _temp;
      router.events.on("routeChangeComplete", handleRouteChangeComplete);
      return () => router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
    t2 = [router.events];
    $[0] = router.events;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(_temp4, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useEffect(_temp6, t4);
  const {
    debugModeEnabled
  } = useDebugModeEnabled();
  const {
    viewModeEnabled
  } = useViewModeFlg();
  const getLayout = Component.getLayout ?? _temp7;
  let t5;
  if ($[5] !== router.pathname) {
    t5 = router.pathname.startsWith("/global-admin");
    $[5] = router.pathname;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const isGlobalAdminScreen = t5;
  let t6;
  if ($[7] !== isGlobalAdminScreen || $[8] !== router.pathname) {
    t6 = router.pathname.startsWith("/admin") || isGlobalAdminScreen;
    $[7] = isGlobalAdminScreen;
    $[8] = router.pathname;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const isAdminScreen = t6;
  const isAuthScreen = router.pathname === "/auth" || router.pathname === "/saml-auth" || router.pathname === "/forgot-password";
  const showViewModeBanner = !isGlobalAdminScreen && viewModeEnabled;
  const showDebugModeBanner = !isGlobalAdminScreen && debugModeEnabled;
  const modeBannerHeight = (showViewModeBanner ? VIEW_MODE_BANNER_HEIGHT : 0) + (showDebugModeBanner ? DEBUG_MODE_BANNER_HEIGHT : 0);
  let t7;
  let t8;
  if ($[10] !== modeBannerHeight) {
    t7 = () => {
      const root = document.documentElement;
      root.style.setProperty("--mode-banner-height", `${modeBannerHeight}px`);
      return () => {
        root.style.setProperty("--mode-banner-height", "0px");
      };
    };
    t8 = [modeBannerHeight];
    $[10] = modeBannerHeight;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  const onError = _temp8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = process.env.NODE_ENV === "test" ? {
      provider: _temp9,
      shouldRetryOnError: false
    } : undefined;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Head><link rel="shortcut icon" href="/favicon.ico" /></Head>;
    t11 = <SignedCookieGate />;
    t12 = <LoadingWithState />;
    t13 = <SnackbarWithState />;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
  } else {
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t13 = $[17];
  }
  let t14;
  if ($[18] !== showViewModeBanner) {
    t14 = showViewModeBanner && <Box bgcolor="#009944" height={VIEW_MODE_BANNER_HEIGHT} display="flex" alignItems="center" justifyContent="center" zIndex={9999} position="sticky" top={0} width="100%"><Typography color="white" variant="h1" align="center" component="div">監視モード</Typography></Box>;
    $[18] = showViewModeBanner;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== showDebugModeBanner || $[21] !== showViewModeBanner) {
    t15 = showDebugModeBanner && <Typography bgcolor="yellow" height={DEBUG_MODE_BANNER_HEIGHT} color="red" fontWeight={600} fontSize={24} textAlign="center" position="sticky" top={showViewModeBanner ? VIEW_MODE_BANNER_HEIGHT : 0}>デバッグモード</Typography>;
    $[20] = showDebugModeBanner;
    $[21] = showViewModeBanner;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== isAdminScreen || $[24] !== isAuthScreen) {
    t16 = !isAdminScreen && !isAuthScreen && <IncidentBanner />;
    $[23] = isAdminScreen;
    $[24] = isAuthScreen;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== Component || $[27] !== pageProps) {
    t17 = <Component {...pageProps} />;
    $[26] = Component;
    $[27] = pageProps;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== getLayout || $[30] !== t17) {
    t18 = getLayout(t17);
    $[29] = getLayout;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t14 || $[33] !== t15 || $[34] !== t16 || $[35] !== t18) {
    t19 = <SWRConfig value={t9}><ErrorBoundary FallbackComponent={DefaultError} onError={onError}><NuqsAdapter><LoadingMutationProvider><SnackbarProvider><ApolloProvider client={client}><OpenFeatureProvider><AssessmentConfigProvider><AssessmentListProvider><ReceiptListProvider><ScrollPositionProvider><SearchParamsContextProvider><SearchParamsContextProviderNew><ConnectDataProvider>{t10}{t11}{t12}{t13}<ThemeProvider theme={theme}><StyledEngineProvider injectFirst={true}>{t14}{t15}{t16}{t18}</StyledEngineProvider></ThemeProvider></ConnectDataProvider></SearchParamsContextProviderNew></SearchParamsContextProvider></ScrollPositionProvider></ReceiptListProvider></AssessmentListProvider></AssessmentConfigProvider></OpenFeatureProvider></ApolloProvider></SnackbarProvider></LoadingMutationProvider></NuqsAdapter></ErrorBoundary></SWRConfig>;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
}
function _temp9() {
  return new Map();
}
function _temp8(error, info) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  console.log("error.message", errorMessage);
  console.log("info.componentStack:", info.componentStack);
  if (!errorMessage.includes("internal server error")) {
    Sentry.captureException(error, {
      extra: {
        componentStack: info.componentStack
      }
    });
  }
}
function _temp7(page) {
  return page;
}
function _temp6() {
  const handlePageHide = _temp5;
  window.addEventListener("pagehide", handlePageHide);
  return () => window.removeEventListener("pagehide", handlePageHide);
}
function _temp5() {
  trackAnalysisEvent(analysisEventKey.PAGE_UNLOADED, {
    url: window.location.href,
    pathname: window.location.pathname
  });
  flushAnalysisEvents();
}
function _temp4() {
  inspectorAuthInteractor.isReadyToUse().then(_temp2).catch(_temp3);
}
function _temp3() {}
function _temp2(signedIn) {
  if (signedIn) {
    initializeFeatureFlags();
  }
}
function _temp() {
  client.cache.gc();
}
const LoadingWithState = () => {
  const $ = _c(2);
  const {
    isLoading
  } = useContext(LoadingContext);
  let t0;
  if ($[0] !== isLoading) {
    t0 = <>{isLoading ? <Loading /> : <div />}</>;
    $[0] = isLoading;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const SnackbarWithState = () => {
  const $ = _c(10);
  const {
    snackbar,
    onClose
  } = useContext(SnackbarContext);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      vertical: "top",
      horizontal: "center"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      marginTop: "calc(var(--mode-banner-height, 0px) + var(--incident-bar-height, 0px) + var(--incident-chip-clearance, 0px) + 24px)"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onClose || $[3] !== snackbar.message || $[4] !== snackbar.severity) {
    t2 = <Alert onClose={onClose} severity={snackbar.severity}>{snackbar.message}</Alert>;
    $[2] = onClose;
    $[3] = snackbar.message;
    $[4] = snackbar.severity;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onClose || $[7] !== snackbar.open || $[8] !== t2) {
    t3 = <Snackbar anchorOrigin={t0} open={snackbar.open} autoHideDuration={5000} onClose={onClose} style={t1}>{t2}</Snackbar>;
    $[6] = onClose;
    $[7] = snackbar.open;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};

// getInitialPropsを使うと、Appコンポーネントのpropsにqueryが渡されます。
// 使わない場合クライアント側でqueryを再生成するため、一時的にstateがundefinedになり、それを避けるための実装です。
MyApp.getInitialProps = async () => ({
  pageProps: {}
});
export default MyApp;